import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '@Helpers';

import { useUpdateScheduleWeekAlertCeiling } from '@CustomHooks';
import { FirmStoreSummary, Schedule, ScheduleWeek } from '@Models';
import AlertCeilingUtilization from './alertCeilingUtilization';
import { canEditScheduleWeek, formatWeekOf, getScheduleComposition, getTotalVisitSlots } from './constants';
import PeriodNavigator from './periodNavigator';
import ScheduleSummaryFacts from './scheduleSummaryFacts';
import useCollapsiblePanel from './useCollapsiblePanel';
import useResizableWidth from './useResizableWidth';

interface ScheduleSidebarProps {
  schedule: Schedule;
  storesCount: number;
  firms: FirmStoreSummary[];
  retailers: string[];
  canManage: boolean;
  weeks: ScheduleWeek[];
  selectedWeekIndex: number;
  onSelectWeek: (index: number) => void;
  isWeeksReady: boolean;
  hasWeeksError: boolean;
  mayNotFitCount: number;
}

const SIDEBAR_MIN_WIDTH = 288;
const SIDEBAR_MAX_WIDTH = 560;
const SIDEBAR_DEFAULT_WIDTH = 340;
const SIDEBAR_COLLAPSED_WIDTH = 52;

const ScheduleSidebar = ({
  schedule,
  storesCount,
  firms,
  retailers,
  canManage,
  weeks,
  selectedWeekIndex,
  onSelectWeek,
  isWeeksReady,
  hasWeeksError,
  mayNotFitCount,
}: ScheduleSidebarProps) => {
  const { t, i18n } = useTranslation();
  const { isCollapsed, collapse, expand } = useCollapsiblePanel();
  const { width, handleResizeStart } = useResizableWidth({
    defaultWidth: SIDEBAR_DEFAULT_WIDTH,
    minWidth: SIDEBAR_MIN_WIDTH,
    maxWidth: SIDEBAR_MAX_WIDTH,
  });
  const composition = getScheduleComposition(schedule.alert_ceiling_pct);
  const showWeeksSection = composition !== 'alerts_only';
  const showCeilingWidget = composition === 'hybrid';
  const updateWeekCeiling = useUpdateScheduleWeekAlertCeiling(schedule.id);
  const selectedWeek = selectedWeekIndex >= 0 ? weeks[selectedWeekIndex] : undefined;

  const saveWeekCeiling = (weekStart: string, alertCeilingPct: number | null) =>
    updateWeekCeiling.mutate(
      { weekStart, alertCeilingPct },
      { onError: () => showErrorToast(t('Schedule.Detail.Sidebar.Utilization.SaveError')) },
    );

  if (isCollapsed) {
    return (
      <aside
        className="flex flex-shrink-0 flex-col items-center gap-3 border-l border-gray-200 bg-white py-4"
        style={{ width: SIDEBAR_COLLAPSED_WIDTH }}>
        <button
          type="button"
          aria-label={t('Schedule.Detail.Sidebar.Expand')}
          onClick={expand}
          className="text-gray-400 hover:text-gray-600">
          <ChevronLeft size={16} />
        </button>
        {selectedWeek && (
          <div
            className="mt-1 text-[10px] font-bold uppercase tracking-[0.09em] text-gray-500"
            style={{ writingMode: 'vertical-rl' }}>
            {selectedWeek.label ??
              t('Schedule.Detail.Sidebar.Period.WeekOf', {
                date: formatWeekOf(selectedWeek.week_start, i18n.language),
              })}
          </div>
        )}
      </aside>
    );
  }

  return (
    <aside className="relative flex flex-shrink-0 flex-col border-l border-gray-200 bg-white" style={{ width }}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t('Schedule.Detail.Sidebar.Resize')}
        onMouseDown={handleResizeStart}
        className="absolute left-0 top-0 h-full w-1 cursor-col-resize"
      />

      <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-3 py-2.5">
        <span className="text-sm font-semibold text-gray-900">{t('Schedule.Detail.Sidebar.ObjectiveTitle')}</span>
        <button
          type="button"
          aria-label={t('Schedule.Detail.Sidebar.Collapse')}
          onClick={collapse}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600">
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Only the content scrolls, so the resize handle above keeps covering the full edge. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <ScheduleSummaryFacts schedule={schedule} storesCount={storesCount} retailers={retailers} firms={firms} />

        {showWeeksSection && isWeeksReady && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-[18px] py-4">
            {selectedWeek ? (
              <>
                <PeriodNavigator weeks={weeks} selectedIndex={selectedWeekIndex} onSelect={onSelectWeek} />
                {showCeilingWidget && (
                  <AlertCeilingUtilization
                    key={selectedWeek.week_start}
                    ceilingPct={selectedWeek.effective_alert_ceiling_pct}
                    isCustomized={selectedWeek.alert_ceiling_override_pct !== null}
                    canManage={canManage && canEditScheduleWeek(selectedWeek)}
                    totalSlots={getTotalVisitSlots(firms)}
                    isSaving={updateWeekCeiling.isPending}
                    onCommit={(value) => saveWeekCeiling(selectedWeek.week_start, value)}
                    onReset={() => saveWeekCeiling(selectedWeek.week_start, null)}
                    mayNotFitCount={mayNotFitCount}
                  />
                )}
              </>
            ) : hasWeeksError ? (
              <p className="text-xs text-red-500">{t('Schedule.Detail.Sidebar.Period.LoadError')}</p>
            ) : (
              <p className="text-xs text-gray-400">{t('Schedule.Detail.Sidebar.Period.NoWeeks')}</p>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};

export default ScheduleSidebar;
